import { createAsyncThunk, createSlice } from '@reduxjs/toolkit';
import {
  obterCaracteristicasDomicilio,
  obterEnderecoFamilia,
  obterInformacoesDetalhadasFamilia,
  obterMembrosFamilia,
} from '../../api/familiaAPI';
import { exportarConsultaCompleta } from '../../api/pdfAPI';
import {
  obterInformacoesDetalhadasPessoa,
  obterRepresentanteLegal,
  obterResponsaveisFamiliares,
} from '../../api/pessoaAPI';
import { RootState } from '../../app/store';

export enum State {
  MENU = 'MENU',
  IDENTIFICACAO_ENDERECO = 'IDENTIFICACAO_ENDERECO',
  CARACTERISTICAS_DOMICILIO = 'CARACTERISTICAS_DOMICILIO',
  INFO_FAMILIA = 'INFO_FAMILIA',
  RESPONSAVEL_FAMILIAR = 'RESPONSAVEL_FAMILIAR',
  REPRESENTANTE_LEGAL = 'REPRESENTANTE_LEGAL',
  MEMBRO_FAMILIAR = 'MEMBRO_FAMILIAR',
  MEMBROS_FAMILIARES = 'MEMBROS_FAMILIARES',
  CARTEIRA_DOCUMENTOS = 'CARTEIRA_DOCUMENTOS',
}

export interface ConsultaCompletaState {
  state: State;
  endereco: any;
  domicilio: any;
  familia: any;
  pessoa: any;
  responsaveisFamiliares: any[];
  representanteLegal: any;
  membrosFamiliares: any[];
  pdf: any;
  familiaTemSolicitacaoObito: boolean;
}

const initialState: ConsultaCompletaState = {
  state: State.MENU,
  endereco: null,
  domicilio: null,
  familia: null,
  pessoa: null,
  responsaveisFamiliares: [],
  representanteLegal: null,
  membrosFamiliares: [],
  pdf: null,
  familiaTemSolicitacaoObito: false,
};

export const getIdentificacaoEndereco = createAsyncThunk(
  'consultaCompleta/getIdentificacaoEndereco',
  async (numeroFamiliar: number) => {
    return obterEnderecoFamilia(numeroFamiliar);
  }
);

export const getCaracteristicasDomicilio = createAsyncThunk(
  'consultaCompleta/getCaracteristicasDomicilio',
  async (numeroFamiliar: number) => {
    return obterCaracteristicasDomicilio(numeroFamiliar);
  }
);

export const getInfoFamilia = createAsyncThunk(
  'consultaCompleta/getInfoFamilia',
  async (numeroFamiliar: number) => {
    return obterInformacoesDetalhadasFamilia(numeroFamiliar);
  }
);

export const getResponsaveisFamiliares = createAsyncThunk(
  'consultaCompleta/getResponsaveisFamiliares',
  async (numeroFamiliar: number) => {
    return obterResponsaveisFamiliares(numeroFamiliar);
  }
);

export const getRepresentanteLegal = createAsyncThunk(
  'consultaCompleta/getRepresentanteLegal',
  async (numeroFamiliar: number) => {
    return obterRepresentanteLegal(numeroFamiliar);
  }
);

export const getDadosRepresentanteLegal = createAsyncThunk(
  'consultaCompleta/getDadosRepresentanteLegal',
  async (numeroFamiliar: number) => {
    return obterRepresentanteLegal(numeroFamiliar);
  }
);

export const getMembrosFamiliares = createAsyncThunk(
  'consultaCompleta/getMembrosFamiliares',
  async (numeroFamiliar: number) => {
    const membrosFamilia = await obterMembrosFamilia(numeroFamiliar);
    return membrosFamilia.integranteFamiliaDTOs;
  }
);

export const getMembrosFamiliaresParaVerificacaoSolicitacaoObito = createAsyncThunk(
  'consultaCompleta/getMembrosFamiliaresParaVerificacaoSolicitacaoObito',
  async (numeroFamiliar: number) => {
    const membrosFamilia = await obterMembrosFamilia(numeroFamiliar);
    return membrosFamilia.integranteFamiliaDTOs;
  }
);

export const getDetalhesMembro = createAsyncThunk(
  'consultaCompleta/getDetalhesMembro',
  async (identificador: number) => {
    return obterInformacoesDetalhadasPessoa(identificador);
  }
);

export const getExportacaoConsultaCompleta = createAsyncThunk(
  'consultaCompleta/getExportacaoConsultaCompleta',
  async (dados: any) => {
    return exportarConsultaCompleta(dados);
  }
);

export const consultaCompletaSlice = createSlice({
  name: 'consultaCompleta',
  initialState,
  reducers: {
    voltarParaMenu: (state) => {
      state.state = State.MENU;
    },
    voltarParaMembros: (state) => {
      state.state = State.MEMBROS_FAMILIARES;
    },
    irParaCarteiraDocumentos: (state) => {
      state.state = State.CARTEIRA_DOCUMENTOS;
    },
    voltarParaMembro: (state) => {
      state.state = State.MEMBRO_FAMILIAR;
    },
    limparPdf: (state) => {
      state.pdf = '';
    },
    limpar: (state) => {
      state.membrosFamiliares = initialState.membrosFamiliares;
    }
  },
  extraReducers: (builder) => {
    builder
      .addCase(getExportacaoConsultaCompleta.fulfilled, (state, action) => {
        state.pdf = action.payload.base64PDF;
      })
      .addCase(getIdentificacaoEndereco.fulfilled, (state, action) => {
        state.state = State.IDENTIFICACAO_ENDERECO;
        state.endereco = action.payload;
      })
      .addCase(getCaracteristicasDomicilio.fulfilled, (state, action) => {
        state.state = State.CARACTERISTICAS_DOMICILIO;
        state.domicilio = action.payload;
      })
      .addCase(getInfoFamilia.fulfilled, (state, action) => {
        state.state = State.INFO_FAMILIA;
        state.familia = action.payload;
      })
      .addCase(getResponsaveisFamiliares.fulfilled, (state, action) => {
        state.state = State.RESPONSAVEL_FAMILIAR;
        state.responsaveisFamiliares = action.payload;
      })
      .addCase(getRepresentanteLegal.fulfilled, (state, action) => {
        state.state = State.REPRESENTANTE_LEGAL;
        state.representanteLegal = action.payload;
      })
      .addCase(getDadosRepresentanteLegal.fulfilled, (state, action) => {
        state.representanteLegal = action.payload;
      })
      .addCase(getDetalhesMembro.fulfilled, (state, action) => {
        state.state = State.MEMBRO_FAMILIAR;
        state.pessoa = action.payload;
      })
      .addCase(getMembrosFamiliares.fulfilled, (state, action) => {
        state.state = State.MEMBROS_FAMILIARES;
        state.membrosFamiliares = action.payload;
        state.familiaTemSolicitacaoObito = action.payload.some(m => m.solicitacaoExclusaoObito);
      })
      .addCase(getMembrosFamiliaresParaVerificacaoSolicitacaoObito.fulfilled, (state, action) => {
        state.membrosFamiliares = action.payload;
        state.familiaTemSolicitacaoObito = action.payload.some(m => m.solicitacaoExclusaoObito);
      });
  },
});

export const { voltarParaMenu, voltarParaMembros, irParaCarteiraDocumentos, voltarParaMembro, limparPdf, limpar } = consultaCompletaSlice.actions;

export const selectState = (state: RootState) => state.consultaCompleta.state;
export const selectEndereco = (state: RootState) => state.consultaCompleta.endereco;
export const selectDomicilio = (state: RootState) => state.consultaCompleta.domicilio;
export const selectFamilia = (state: RootState) => state.consultaCompleta.familia;
export const selectPessoa = (state: RootState) => state.consultaCompleta.pessoa;
export const selectRepresentanteLegal = (state: RootState) => state.consultaCompleta.representanteLegal;
export const selectResponsaveisFamiliares = (state: RootState) => state.consultaCompleta.responsaveisFamiliares;
export const selectMembrosFamiliares = (state: RootState) => state.consultaCompleta.membrosFamiliares;
export const selectPdf = (state: RootState) => state.consultaCompleta.pdf;
export const selectFamiliaTemSolicitacaoObito = (state: RootState) => state.consultaCompleta.familiaTemSolicitacaoObito;

export default consultaCompletaSlice.reducer;
